import { BUBBLE_LOADER_DEFAULTS } from "./config";

export type Point = readonly [number, number];
export type BubbleGroup = {
  colorIndex: number;
  center: Point;
  points: Point[];
  edges: [number, number][];
};

export function bubbleGroups(
  spacing = BUBBLE_LOADER_DEFAULTS.spacing,
): BubbleGroup[] {
  const s = spacing;
  const h = (s * Math.sqrt(3)) / 2;
  return [
    {
      colorIndex: 0,
      center: [-s, 0],
      points: [
        [-1.5 * s, h],
        [-0.5 * s, h],
        [0, 0],
        [-0.5 * s, -h],
        [-1.5 * s, -h],
        [-2 * s, 0],
      ],
      edges: [
        [0, 1],
        [1, 2],
        [2, 3],
        [3, 4],
        [4, 5],
        [5, 0],
      ],
    },
    {
      colorIndex: 1,
      center: [(2 * s) / 3, 0],
      points: [
        [0.5 * s, h],
        [s, 0],
        [0.5 * s, -h],
      ],
      edges: [
        [0, 1],
        [1, 2],
      ],
    },
    {
      colorIndex: 2,
      center: [(5 * s) / 3, 0],
      points: [
        [1.5 * s, h],
        [2 * s, 0],
        [1.5 * s, -h],
      ],
      edges: [
        [0, 1],
        [1, 2],
      ],
    },
  ];
}

/** Solve y = waist + a(cosh(x/a) - 1), tangent to two unit circles. */
export function solveCatenary(distance: number, waist: number) {
  if (!(distance > 2 && waist > -4 && waist < 3)) {
    throw new RangeError(
      "Catenary requires separated unit circles and a signed waist between -4 and 3.",
    );
  }
  // Above the circle tops, an inverted catenary meets their outer shoulders.
  // At exactly one radius the limiting curve is a horizontal tangent.
  if (waist === 1) {
    return { halfSpan: distance / 2, a: 1e12, waist, angle: Math.PI / 2 };
  }
  const direction = waist > 1 ? -1 : 1;
  let low = 0.001;
  let high = Math.PI / 2 - 0.001;
  for (let i = 0; i < 48; i++) {
    const angle = (low + high) / 2;
    const halfSpan = distance / 2 - direction * Math.cos(angle);
    const a = (direction * halfSpan) / Math.asinh(1 / Math.tan(angle));
    const solvedWaist = Math.sin(angle) - a * (1 / Math.sin(angle) - 1);
    if (direction * (solvedWaist - waist) < 0) {
      low = angle;
    } else {
      high = angle;
    }
  }
  const angle = (low + high) / 2;
  const halfSpan = distance / 2 - direction * Math.cos(angle);
  return {
    halfSpan,
    a: (direction * halfSpan) / Math.asinh(1 / Math.tan(angle)),
    waist,
    angle,
  };
}

/** Cubic Hermite approximation of the actual catenary, with tangent endpoints. */
export function connectorPath(from: Point, to: Point, waist: number) {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const distance = Math.hypot(dx, dy);
  const { halfSpan, a } = solveCatenary(distance, waist);
  const ux = dx / distance;
  const uy = dy / distance;
  const point = (x: number, y: number) => {
    const along = x + distance / 2;
    return `${(from[0] + along * ux - y * uy).toFixed(6)} ${(from[1] + along * uy + y * ux).toFixed(6)}`;
  };
  const height = (x: number) => waist + a * (Math.cosh(x / a) - 1);
  const result = [`M ${point(-halfSpan, -height(-halfSpan))}`];
  for (const side of [-1, 1]) {
    if (side === 1) {
      result.push(`L ${point(halfSpan, height(halfSpan))}`);
    }
    for (let i = 0; i < 12; i++) {
      const x0 = side * (-halfSpan + (2 * halfSpan * (12 - i)) / 12);
      const x1 = side * (-halfSpan + (2 * halfSpan * (11 - i)) / 12);
      const step = x1 - x0;
      result.push(
        `C ${point(x0 + step / 3, side * (height(x0) + (Math.sinh(x0 / a) * step) / 3))} ${point(x1 - step / 3, side * (height(x1) - (Math.sinh(x1 / a) * step) / 3))} ${point(x1, side * height(x1))}`,
      );
    }
  }
  return `${result.join(" ")} Z`;
}
